import { useEffect, useState } from 'preact/hooks';
import { ChipCount } from '../components/ChipCount';
import {
  ALL_STATUSES,
  filterSuppliers,
  mergeSuppliers,
  type AllStatus,
} from '../../shared/suppliers/all';
import type {
  SupplierHistoryBody,
  SupplierListBody,
  SupplierRow,
} from '../../shared/suppliers/review';
import type {
  InviteIssuedBody,
  InviteListBody,
  InviteRow,
  InviteStatus,
} from '../../shared/suppliers/invites';
import { useI18n, type MessageKey } from '../i18n';
import { ApiError, apiGet, apiSend } from '../lib/api';
import { formatDate } from '../lib/dates';

// Suppliers (screen 17), Procurement only. All suppliers (D79): registered ones and those known
// only from the history import, searchable, with an invite for the history ones. P2.5:
// registrations waiting for review, approved and rejected suppliers. P2.3: invites; an invite
// link is shown once, when it is created or resent.

type Tab = 'all' | 'waiting' | 'approved' | 'rejected' | 'invites';
const STATUS_TONE: Record<AllStatus, string> = {
  approved: 'success',
  submitted: 'info',
  rejected: 'danger',
  history: 'neutral',
};
const STATUS_LABEL: Record<AllStatus, MessageKey> = {
  approved: 'sup.status.approved',
  submitted: 'sup.tab.waiting',
  rejected: 'sup.status.rejected',
  history: 'sup.all.history',
};

interface Prefill {
  company: string;
  category: string;
}

type ListLoad =
  | { kind: 'loading' }
  | { kind: 'error'; forbidden: boolean; reference: string | null }
  | { kind: 'ok'; data: SupplierListBody };

export function SuppliersPage() {
  const { t, lang } = useI18n();
  const [load, setLoad] = useState<ListLoad>({ kind: 'loading' });
  const [tab, setTab] = useState<Tab>('all');
  const [prefill, setPrefill] = useState<Prefill | null>(null);
  const [history, setHistory] = useState<SupplierHistoryBody | null>(null);
  const [historyFailed, setHistoryFailed] = useState(false);
  const [q, setQ] = useState('');
  const [show, setShow] = useState<AllStatus | ''>('');

  useEffect(() => {
    let live = true;
    apiGet<SupplierListBody>('/api/suppliers')
      .then((data) => live && setLoad({ kind: 'ok', data }))
      .catch((err: unknown) => {
        if (!live) return;
        const e = err instanceof ApiError ? err : null;
        setLoad({
          kind: 'error',
          forbidden: e?.code === 'forbidden',
          reference: e?.correlationId ?? null,
        });
      });
    apiGet<SupplierHistoryBody>('/api/suppliers/history')
      .then((h) => live && setHistory(h))
      .catch(() => live && setHistoryFailed(true));
    return () => {
      live = false;
    };
  }, []);

  const data = load.kind === 'ok' ? load.data : null;
  const rows = (status: SupplierRow['status']) =>
    data?.suppliers.filter((s) => s.status === status) ?? [];
  const tabs: Tab[] = ['all', 'waiting', 'approved', 'rejected', 'invites'];
  const catName = (id: string) => {
    const c = data?.categories.find((x) => x.id === id);
    return c ? (lang === 'ar' ? c.nameAR || c.nameEN : c.nameEN) : id;
  };
  const place = (s: SupplierRow) =>
    [s.city, s.country ? t(`reg.country.${s.country}` as MessageKey) : '']
      .filter(Boolean)
      .join(', ');
  const all = data ? mergeSuppliers(data.suppliers, history?.suppliers ?? [], place) : [];
  const counts: Record<Tab, number | null> = {
    all: data && (history || historyFailed) ? all.length : null,
    waiting: rows('submitted').length,
    approved: rows('approved').length,
    rejected: rows('rejected').length,
    invites: null,
  };
  const hits = filterSuppliers(all, q, show, catName);

  return (
    <div class="stack">
      <div class="admin-tabs" role="group" aria-label={t('sup.tabs')}>
        {tabs.map((id) => (
          <button
            key={id}
            type="button"
            class="chip"
            aria-pressed={tab === id}
            onClick={() => {
              setPrefill(null);
              setTab(id);
            }}
          >
            {t(`sup.tab.${id}` as MessageKey)}
            {data && <ChipCount n={counts[id]} />}
          </button>
        ))}
      </div>
      {tab === 'invites' ? (
        <InvitesPanel key={prefill?.company ?? ''} prefill={prefill} />
      ) : tab === 'all' && data ? (
        <section class="card stack" aria-labelledby="sup-all">
          <h2 id="sup-all" class="card__title">
            {t('sup.tab.all')}
          </h2>
          <p class="caption">{t('sup.all.intro')}</p>
          {historyFailed && (
            <p class="notice notice--action" role="alert">
              {t('sup.all.historyFailed')}
            </p>
          )}
          <div class="queue-filters">
            <div class="field">
              <label for="sup-all-q">{t('sup.history.search')}</label>
              <input
                id="sup-all-q"
                type="search"
                value={q}
                onInput={(e) => setQ((e.target as HTMLInputElement).value)}
              />
            </div>
            <div class="field">
              <label for="sup-all-status">{t('sup.all.show')}</label>
              <select
                id="sup-all-status"
                value={show}
                onChange={(e) => setShow((e.target as HTMLSelectElement).value as AllStatus | '')}
              >
                <option value="">{t('sup.all.every')}</option>
                {ALL_STATUSES.map((st) => (
                  <option key={st} value={st}>
                    {t(STATUS_LABEL[st])}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <p class="caption" aria-live="polite">
            {t('sup.history.count', { shown: String(hits.length), total: String(all.length) })}
          </p>
          {!history && !historyFailed && <p aria-busy="true">{t('req.loading')}</p>}
          {hits.length === 0 ? (
            <p>{t('sup.none.history')}</p>
          ) : (
            <div class="table-wrap">
              <table class="table">
                <thead>
                  <tr>
                    <th scope="col">{t('inv.col.supplier')}</th>
                    <th scope="col">{t('sup.col.categories')}</th>
                    <th scope="col">{t('sup.col.status')}</th>
                    <th scope="col">
                      <span class="visually-hidden">{t('admin.actions')}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {hits.map((s) => (
                    <tr key={s.supplierId}>
                      <td>
                        {s.status === 'history' ? (
                          <span class="req-row__title">{s.name}</span>
                        ) : (
                          <a class="req-row__title" href={`#/suppliers/${s.supplierId}`}>
                            {s.name}
                          </a>
                        )}
                        <span class="caption cell-sub">{s.sub || '—'}</span>
                      </td>
                      <td>{s.categories.map(catName).join(', ') || '—'}</td>
                      <td>
                        <span class={`pill pill--${STATUS_TONE[s.status]}`}>
                          {t(STATUS_LABEL[s.status])}
                        </span>
                      </td>
                      <td>
                        {s.status === 'history' && (
                          <button
                            type="button"
                            class="button button--small"
                            aria-label={t('sup.history.inviteFor', { name: s.name })}
                            onClick={() => {
                              setPrefill({ company: s.name, category: s.categories[0] ?? '' });
                              setTab('invites');
                            }}
                          >
                            {t('sup.history.invite')}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ) : (
        <section class="card" aria-label={t(`sup.tab.${tab}` as MessageKey)}>
          {load.kind === 'loading' && <p aria-busy="true">{t('req.loading')}</p>}
          {load.kind === 'error' && (
            <div role="alert">
              <p>{t(load.forbidden ? 'sup.error.forbidden' : 'req.loadError')}</p>
              {load.reference && (
                <p class="caption" dir="ltr">
                  {t('error.reference', { ref: load.reference })}
                </p>
              )}
            </div>
          )}
          {data &&
            tab !== 'all' &&
            (rows(tab === 'waiting' ? 'submitted' : tab).length === 0 ? (
              <p>{t(`sup.none.${tab}` as MessageKey)}</p>
            ) : (
              <div class="table-wrap">
                <table class="table">
                  <thead>
                    <tr>
                      <th scope="col">{t('inv.col.supplier')}</th>
                      <th scope="col">{t('sup.col.categories')}</th>
                      <th scope="col">
                        {t(tab === 'waiting' ? 'sup.col.submitted' : 'sup.col.bank')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows(tab === 'waiting' ? 'submitted' : tab).map((s) => (
                      <tr key={s.supplierId}>
                        <td>
                          <a class="req-row__title" href={`#/suppliers/${s.supplierId}`}>
                            {s.name}
                          </a>
                          <span class="caption cell-sub">
                            <span dir="ltr">{s.supplierId}</span> · {place(s)}
                          </span>
                        </td>
                        <td>{s.categories.map(catName).join(', ') || '—'}</td>
                        <td>
                          {tab === 'waiting' ? (
                            s.submittedAt ? (
                              formatDate(s.submittedAt, lang)
                            ) : (
                              '—'
                            )
                          ) : (
                            <span class={`pill pill--${s.bankConfirmed ? 'success' : 'warning'}`}>
                              {t(s.bankConfirmed ? 'sup.bank.confirmed' : 'sup.bank.missing')}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
        </section>
      )}
    </div>
  );
}

type Load =
  | { kind: 'loading' }
  | { kind: 'error'; forbidden: boolean; reference: string | null }
  | { kind: 'ok'; data: InviteListBody };

const TONE: Record<InviteStatus, string> = {
  sent: 'info',
  opened: 'info',
  submitted: 'success',
  revoked: 'neutral',
  expired: 'warning',
};

function problemText(err: unknown, t: (k: MessageKey) => string): string {
  const e = err instanceof ApiError ? err : null;
  const known = ['invite_exists', 'invalid_transition', 'conflict', 'invalid_input', 'forbidden'];
  const key = (
    known.includes(e?.code ?? '') ? `inv.error.${e?.code ?? ''}` : 'review.error.generic'
  ) as MessageKey;
  return t(key);
}

function InvitesPanel({ prefill }: { prefill: Prefill | null }) {
  const { t, lang } = useI18n();
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [reload, setReload] = useState(0);
  const [company, setCompany] = useState(prefill?.company ?? '');
  const [email, setEmail] = useState('');
  const [category, setCategory] = useState(prefill?.category ?? '');
  const [sendEmail, setSendEmail] = useState(true);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [problem, setProblem] = useState<string | null>(null);
  const [issued, setIssued] = useState<InviteIssuedBody | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let live = true;
    apiGet<InviteListBody>('/api/supplier-invites')
      .then((data) => live && setLoad({ kind: 'ok', data }))
      .catch((err: unknown) => {
        if (!live) return;
        const e = err instanceof ApiError ? err : null;
        setLoad({
          kind: 'error',
          forbidden: e?.code === 'forbidden',
          reference: e?.correlationId ?? null,
        });
      });
    return () => {
      live = false;
    };
  }, [reload]);

  async function create(e: Event) {
    e.preventDefault();
    setBusy(true);
    setProblem(null);
    setErrors({});
    setIssued(null);
    setCopied(false);
    try {
      const out = await apiSend<InviteIssuedBody>('POST', '/api/supplier-invites', {
        company,
        email,
        category,
        sendEmail,
      });
      setIssued(out);
      setCompany('');
      setEmail('');
      setCategory('');
      setReload((r) => r + 1);
    } catch (err) {
      setProblem(problemText(err, t));
      if (err instanceof ApiError) setErrors(err.fields);
    } finally {
      setBusy(false);
    }
  }

  async function act(row: InviteRow, action: 'resend' | 'revoke') {
    if (action === 'revoke' && !confirm(t('inv.confirmRevoke'))) return;
    setProblem(null);
    setIssued(null);
    setCopied(false);
    try {
      const out = await apiSend<InviteIssuedBody | { invite: InviteRow }>(
        'POST',
        `/api/supplier-invites/${encodeURIComponent(row.inviteId)}/action`,
        action === 'resend'
          ? { action, etag: row.etag, sendEmail: true }
          : { action, etag: row.etag },
      );
      if ('link' in out) setIssued(out);
      setReload((r) => r + 1);
    } catch (err) {
      setProblem(problemText(err, t));
    }
  }

  async function copy(link: string) {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  const data = load.kind === 'ok' ? load.data : null;
  const catName = (id: string) => {
    const c = data?.categories.find((x) => x.id === id);
    return c ? (lang === 'ar' ? c.nameAR || c.nameEN : c.nameEN) : id || '—';
  };

  return (
    <div class="pr-layout">
      <section class="card" aria-labelledby="inv-list">
        <h2 id="inv-list" class="card__title">
          {t('inv.invited')}
        </h2>
        {load.kind === 'loading' && <p aria-busy="true">{t('req.loading')}</p>}
        {load.kind === 'error' && (
          <div role="alert">
            <p>{t(load.forbidden ? 'review.error.forbidden' : 'req.loadError')}</p>
            {load.reference && (
              <p class="caption" dir="ltr">
                {t('error.reference', { ref: load.reference })}
              </p>
            )}
          </div>
        )}
        {data &&
          (data.invites.length === 0 ? (
            <p>{t('inv.none')}</p>
          ) : (
            <div class="table-wrap">
              <table class="table">
                <thead>
                  <tr>
                    <th scope="col">{t('inv.col.supplier')}</th>
                    <th scope="col">{t('req.f.category')}</th>
                    <th scope="col">{t('inv.col.status')}</th>
                    <th scope="col">{t('inv.col.expires')}</th>
                    <th scope="col">
                      <span class="visually-hidden">{t('admin.actions')}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.invites.map((i) => (
                    <tr key={i.inviteId}>
                      <td>
                        <span class="req-row__title">{i.company}</span>
                        <span class="caption cell-sub" dir="ltr">
                          {i.email}
                        </span>
                      </td>
                      <td>{catName(i.category)}</td>
                      <td>
                        <span class={`pill pill--${TONE[i.status]}`}>
                          {t(`inv.status.${i.status}` as MessageKey)}
                        </span>
                      </td>
                      <td>{i.expiresAt ? formatDate(i.expiresAt, lang) : '—'}</td>
                      <td>
                        {i.status !== 'submitted' && i.status !== 'revoked' && (
                          <div class="actions">
                            <button
                              type="button"
                              class="button button--small"
                              aria-label={t('inv.resendFor', { name: i.company })}
                              onClick={() => void act(i, 'resend')}
                            >
                              {t('inv.resend')}
                            </button>
                            <button
                              type="button"
                              class="button button--small button--danger"
                              aria-label={t('inv.revokeFor', { name: i.company })}
                              onClick={() => void act(i, 'revoke')}
                            >
                              {t('inv.revoke')}
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
      </section>

      <aside class="stack">
        <form
          class="card stack"
          onSubmit={(e) => void create(e)}
          noValidate
          aria-labelledby="inv-title"
        >
          <h2 id="inv-title" class="card__title">
            {t('inv.title')}
          </h2>
          {problem && (
            <p class="form-problem" role="alert">
              {problem}
            </p>
          )}
          <div class="field">
            <label for="inv-company">{t('inv.company')}</label>
            <input
              id="inv-company"
              type="text"
              maxLength={200}
              aria-invalid={errors.company ? true : undefined}
              value={company}
              onInput={(e) => setCompany((e.target as HTMLInputElement).value)}
            />
          </div>
          <div class="field">
            <label for="inv-email">{t('inv.email')}</label>
            <input
              id="inv-email"
              type="email"
              dir="ltr"
              maxLength={120}
              aria-invalid={errors.email ? true : undefined}
              value={email}
              onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
            />
            {errors.email && <p class="field__error">{errors.email}</p>}
          </div>
          <div class="field">
            <label for="inv-category">{t('inv.category')}</label>
            <select
              id="inv-category"
              value={category}
              onChange={(e) => setCategory((e.target as HTMLSelectElement).value)}
            >
              <option value="">{t('pr.select')}</option>
              {(data?.categories ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {lang === 'ar' ? c.nameAR || c.nameEN : c.nameEN}
                </option>
              ))}
            </select>
          </div>
          <div class="field field--check">
            <input
              id="inv-send"
              type="checkbox"
              checked={sendEmail}
              onChange={(e) => setSendEmail((e.target as HTMLInputElement).checked)}
            />
            <label for="inv-send">{t('inv.sendEmail')}</label>
          </div>
          <button type="submit" class="button button--primary" disabled={busy}>
            {t('inv.create')}
          </button>
          <p class="caption">{t('inv.hint')}</p>
        </form>

        {issued && (
          <section class="card card--emphasis stack" aria-labelledby="inv-link" role="status">
            <h2 id="inv-link" class="card__title">
              {t('inv.linkFor', { name: issued.invite.company })}
            </h2>
            <p class="notice notice--info">{t('inv.linkOnce')}</p>
            <input
              class="inv-link"
              type="text"
              dir="ltr"
              readOnly
              aria-label={t('inv.link')}
              value={issued.link}
              onFocus={(e) => (e.target as HTMLInputElement).select()}
            />
            <div class="actions">
              <button type="button" class="button" onClick={() => void copy(issued.link)}>
                {copied ? t('inv.copied') : t('inv.copy')}
              </button>
              <a class="button" href={issued.whatsapp} target="_blank" rel="noopener noreferrer">
                {t('inv.whatsapp')}
              </a>
            </div>
            {issued.emailed && (
              <p class="caption">{t('inv.emailed', { email: issued.invite.email })}</p>
            )}
          </section>
        )}
      </aside>
    </div>
  );
}
